// Locale-aware formatting shared by server and client components. The locale is
// always explicit, so server and browser render the same text (no hydration mismatch).

export function localeFor(lang: string): string {
  return lang === 'fr' ? 'fr-CA' : 'en-CA';
}

// Building an Intl formatter is expensive - it loads the locale's data - and
// using one is cheap. They were built on every call: a calculator formats
// hundreds of figures per render and spent 0.3 s of a phone's main thread on
// it, a listing page 0.2 s (measured 2026-09-23, CPU x4). Now each locale and
// set of options is built once and reused.
//
// The first formatter a page builds also loads the browser's locale data
// once: 400-590 ms of the main thread at CPU x4, in the middle of hydration,
// on every page that shows a count or a price (measured 2026-09-25). The
// site only ever needs plain numbers and Canadian dollars in en-CA and
// fr-CA, so those are written here without Intl, character for character
// what Intl writes (lib/__tests__/fast-number-format.test.ts compares them);
// anything else still goes to Intl.
export type NumberFormatter = { format(value: number): string };

const numberFormats = new Map<string, NumberFormatter>();
const dateTimeFormats = new Map<string, Intl.DateTimeFormat>();

const NBSP = ' ';
const PLAIN_KEYS = new Set(['style', 'currency', 'minimumFractionDigits', 'maximumFractionDigits']);

// Adds one to a string of digits: "0999" -> "1000".
function incrementDigits(digits: string): string {
  const out = digits.split('');
  for (let i = out.length - 1; i >= 0; i--) {
    if (out[i] !== '9') {
      out[i] = String(Number(out[i]) + 1);
      return out.join('');
    }
    out[i] = '0';
  }
  return '1' + out.join('');
}

function fastFormatter(locale: string, options: Intl.NumberFormatOptions = {}): NumberFormatter | null {
  // en-US writes plain numbers exactly as en-CA does (its dollars differ).
  if (locale === 'en-US' && !options.style && !options.currency) locale = 'en-CA';
  if (locale !== 'en-CA' && locale !== 'fr-CA') return null;
  if (Object.keys(options).some((k) => !PLAIN_KEYS.has(k))) return null;
  const currency = options.style === 'currency';
  if (options.style && options.style !== 'decimal' && !currency) return null;
  if (currency ? options.currency !== 'CAD' : options.currency !== undefined) return null;
  const defMin = currency ? 2 : 0;
  const defMax = currency ? 2 : 3;
  const max = options.maximumFractionDigits ?? Math.max(defMax, options.minimumFractionDigits ?? 0);
  const min = options.minimumFractionDigits ?? Math.min(defMin, max);
  if (!(Number.isInteger(min) && Number.isInteger(max) && min >= 0 && max <= 20 && min <= max)) return null;
  const fr = locale === 'fr-CA';
  const group = fr ? NBSP : ',';
  const point = fr ? ',' : '.';

  return {
    format(value: number): string {
      const n = Number(value);
      const abs = Math.abs(n);
      const text = String(abs);
      // Exponent notation, infinities, NaN: rare enough to leave to Intl.
      if (!Number.isFinite(n) || text.includes('e')) return new Intl.NumberFormat(locale, options).format(n);
      const [intRaw, fracRaw = ''] = text.split('.');
      // Round half away from zero on the shortest decimal form, as Intl does.
      let digits = intRaw + fracRaw.slice(0, max).padEnd(max, '0');
      if (fracRaw.length > max && fracRaw.charCodeAt(max) >= 53) digits = incrementDigits(digits);
      let intPart = digits.slice(0, digits.length - max) || '0';
      let frac = digits.slice(digits.length - max);
      while (frac.length > min && frac.endsWith('0')) frac = frac.slice(0, -1);
      intPart = intPart.replace(/^0+(?=\d)/, '');
      const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, group);
      const body = frac ? `${grouped}${point}${frac}` : grouped;
      const minus = n < 0 || Object.is(n, -0) ? '-' : '';
      if (!currency) return minus + body;
      return fr ? `${minus}${body}${NBSP}$` : `${minus}$${body}`;
    },
  };
}

export function numberFormat(locale: string, options?: Intl.NumberFormatOptions): NumberFormatter {
  const key = `${locale}|${options ? JSON.stringify(options) : ''}`;
  let f = numberFormats.get(key);
  if (!f) {
    f = fastFormatter(locale, options) ?? new Intl.NumberFormat(locale, options);
    numberFormats.set(key, f);
  }
  return f;
}

export function dateTimeFormat(locale: string, options?: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}|${options ? JSON.stringify(options) : ''}`;
  let f = dateTimeFormats.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, options);
    dateTimeFormats.set(key, f);
  }
  return f;
}

// $1,250,000 in English, 1 250 000 $ in French.
export function formatPrice(value: unknown, lang: string): string {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return lang === 'fr' ? 'Prix sur demande' : 'Price on request';
  return numberFormat(localeFor(lang), { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 })
    .format(num)
    .replace('CA$', '$')
    .replace('US$', '$');
}

// Any money amount, including zero: $2,301 in English, 2 301 $ in French. Use this
// for computed figures - a mortgage payment, a tax, a fee. formatPrice is for a
// listing's asking price, where a missing or zero value means "price on request";
// a calculator showing 0 means zero.
export function formatMoney(value: unknown, lang: string, fractionDigits = 0): string {
  // Number(null) and Number('') are both 0, so an absent amount would print as
  // "$0" - a figure the reader would believe. Nothing is the honest output.
  if (value === null || value === undefined || value === '') return '';
  const num = Number(value);
  if (!Number.isFinite(num)) return '';
  return numberFormat(localeFor(lang), {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })
    .format(num)
    .replace('CA$', '$')
    .replace('US$', '$');
}

export function formatNumber(value: unknown, lang: string): string {
  const num = Number(value);
  return Number.isFinite(num) ? numberFormat(localeFor(lang)).format(num) : '';
}

const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

// "September 25, 2026" / "25 septembre 2026" in the visitor's time zone -
// Intl's { day: 'numeric', month: 'long', year: 'numeric' }, written without
// Intl for the same reason as the numbers above.
export function longDate(date: Date, lang: string): string {
  const d = date.getDate(), m = date.getMonth(), y = date.getFullYear();
  return lang === 'fr' ? `${d} ${MONTHS_FR[m]} ${y}` : `${MONTHS_EN[m]} ${d}, ${y}`;
}

export function formatDate(value: unknown, lang: string): string {
  if (!value) return '';
  const date = new Date(value as string);
  if (Number.isNaN(date.getTime())) return '';
  return longDate(date, lang);
}

// Date plus time, for logs and moderation queues where the hour matters.
export function formatDateTime(value: unknown, lang: string): string {
  if (!value) return '';
  const date = new Date(value as string);
  if (Number.isNaN(date.getTime())) return '';
  return dateTimeFormat(localeFor(lang), {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(date);
}

// Bedrooms/bathrooms are stored as DECIMAL(4,1) to allow halves (2.5 baths).
// Whole numbers print without the trailing ".0"; halves keep one decimal.
export function formatRoomCount(value: unknown): string {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  return Number.isInteger(num) ? String(num) : num.toFixed(1);
}
